import { Container, FillGradient, Graphics, Ticker } from 'pixi.js';

export const GAP = 4;
const TILE_RADIUS_MAX = 8;
// Scales down with the tile itself, so small tiles (e.g. on mobile) don't
// end up looking pill-shaped next to a corner radius sized for a big tile.
const tileRadius = (s: number): number => Math.min(TILE_RADIUS_MAX, s * 0.12);
// Same idea for the tile's edge stroke — a bit heavier than before, but
// scaled so it doesn't turn into a thick band on small mobile tiles.
const tileBorder = (s: number): number => Math.min(3.5, Math.max(2.4, s * 0.025 + 1));
const ANIM_DURATION = 230;
const PULSE_DUR_MS  = 600;

// Every tile used to carry a couple of blurred, additively-blended glow
// layers bleeding past its own edges. That's the classic "neon" look, but
// GPU blur filters running on every tile every frame is genuinely heavy —
// it's what made the game sluggish and hot on phones — and one of those
// filters was also occasionally leaving a faint rendering ghost at its
// texture bounds. Tiles are flat fills/strokes now, no filters at all.
const sheen = new FillGradient({
  type: 'linear',
  start: { x: 0, y: 0 },
  end: { x: 0.25, y: 1 },
  colorStops: [
    { offset: 0,   color: 'rgba(255,255,255,0.4)' },
    { offset: 0.5, color: 'rgba(255,255,255,0.05)' },
    { offset: 1,   color: 'rgba(255,255,255,0)' },
  ],
  textureSpace: 'local',
});

// How far above the pointer a dragged tile rides, in tile widths. Half a tile
// puts its bottom edge on the pointer, so a fingertip never covers it.
const DRAG_LIFT = 0.5;

export class Tile {
  gridPos: number;
  private activeCells: number[];
  private gridWidth: number;
  container: Container;
  bg: Graphics;
  overlay: Graphics;
  deco: Graphics;
  private _tileSize: number;
  private _color: number;
  private _selected: boolean;
  private _isAnchor: boolean;
  private _preset: boolean;
  private _locked: boolean;
  private _hover: boolean;
  private _dropTarget: boolean;

  constructor(gridPos: number, activeCells: number[], gridWidth: number) {
    this.gridPos     = gridPos;
    this.activeCells = activeCells;
    this.gridWidth   = gridWidth;

    this.container = new Container();
    this.bg      = new Graphics();
    this.overlay = new Graphics();
    this.deco    = new Graphics();

    this.container.addChild(this.bg);
    this.container.addChild(this.overlay);
    this.container.addChild(this.deco);

    this.container.eventMode = 'static';
    this.container.cursor = 'pointer';

    this._tileSize = 0;
    this._color    = 0x333333;
    this._selected = false;
    this._isAnchor = false;
    this._preset     = false;
    this._locked     = false;
    this._hover      = false;
    this._dropTarget = false;

    this.container.on('pointerover', () => {
      if (this._isAnchor || this._preset || this._locked) return;
      this._hover = true;
      this._redrawOverlay();
    });
    this.container.on('pointerout', () => {
      this._hover = false;
      this._redrawOverlay();
    });
  }

  // Convert an active-cell index to pixel position within the grid container
  gridToPixel(activeIndex: number): { x: number; y: number } {
    const flatPos = this.activeCells[activeIndex];
    const col = flatPos % this.gridWidth;
    const row = Math.floor(flatPos / this.gridWidth);
    return {
      x: col * (this._tileSize + GAP),
      y: row * (this._tileSize + GAP)
    };
  }

  layout(tileSize: number): void {
    this._tileSize = tileSize;
    this.redraw();
    this.snapToGrid();
  }

  snapToGrid(): void {
    const { x, y } = this.gridToPixel(this.gridPos);
    this.container.x = x;
    this.container.y = y;
  }

  setColor(hexColor: number, isAnchor: boolean): void {
    this._color    = hexColor;
    this._isAnchor = isAnchor;
    this.redraw();
  }

  setSelected(selected: boolean): void {
    this._selected = selected;
    this._redrawOverlay();
  }

  setLocked(locked: boolean): void {
    this._locked = locked;
    this._updateInteractive();
    this._redrawOverlay();
  }

  /**
   * A tile that came pre-filled — an edges board's interior. It's drawn set
   * into the board, a little smaller than the tiles around it with a fainter
   * edge, which says "not yours to move" without putting a marker on it. Its
   * colour is untouched: the whole board still has to read as one gradient.
   */
  setPreset(preset: boolean): void {
    if (this._preset === preset) return;
    this._preset = preset;
    this._updateInteractive();
    this.redraw();
  }

  private _updateInteractive(): void {
    const inert = this._locked || this._preset;
    this.container.eventMode = inert ? 'none' : 'static';
    this.container.cursor    = inert ? 'default' : 'pointer';
  }

  /** Mark this tile as the one a dragged tile would land on. */
  setDropTarget(isTarget: boolean): void {
    if (this._dropTarget === isTarget) return;
    this._dropTarget = isTarget;
    this._redrawOverlay();
  }

  /** Draw above its neighbours — used while a tile is being dragged. */
  bringToFront(): void {
    this.container.parent?.addChild(this.container);
  }

  /** Centre the tile on a point in grid space, riding above it. */
  dragTo(x: number, y: number): void {
    const s = this._tileSize;
    this.container.x = x - s / 2;
    this.container.y = y - s / 2 - s * DRAG_LIFT;
  }

  redraw(): void {
    const s = this._tileSize;
    if (s === 0) return;

    const inset  = this._preset ? Math.max(2, s * 0.09) : 0;
    const face   = s - inset * 2;
    const radius = tileRadius(face);

    const bw = tileBorder(face);
    this.bg.clear();
    this.bg.roundRect(inset, inset, face, face, radius).fill(this._color);
    this.bg.roundRect(inset, inset, face, face, radius).fill(sheen);
    this.bg.roundRect(inset + bw / 2, inset + bw / 2, face - bw, face - bw, radius)
      .stroke({ color: 0xFFFFFF, width: bw, alpha: this._preset ? 0.2 : 0.5 });

    this.deco.clear();
    if (this._isAnchor) {
      const cx = s / 2;
      const cy = s / 2;
      const ringR = s * 0.17;
      const ringW = Math.max(2, s * 0.045);

      this.deco.circle(cx, cy, ringR)
        .stroke({ color: 0xFFFFFF, width: ringW, alpha: 0.95 });
    }

    this._redrawOverlay();
  }

  private _redrawOverlay(): void {
    const s = this._tileSize;
    if (s === 0) return;
    this.overlay.clear();
    const radius = tileRadius(s);

    if (this._locked) {
      const m  = s * 0.18;
      const cx = s - m;
      const cy = s - m;
      const r  = s * 0.13;
      this.overlay.circle(cx, cy, r).fill({ color: 0xFFFFFF, alpha: 0.22 });
      this.overlay.circle(cx, cy, r * 0.55).fill({ color: 0xFFFFFF, alpha: 0.55 });
    } else if (this._selected) {
      // Brighten the face itself, then a crisp ring on top — no glow
      // bleeding past the tile's own edges anymore.
      this.overlay
        .roundRect(0, 0, s, s, radius)
        .fill({ color: 0xFFFFFF, alpha: 0.3 });
      this.overlay
        .roundRect(0.5, 0.5, s - 1, s - 1, radius)
        .stroke({ color: 0xFFFFFF, width: 4, alpha: 0.8 });
    } else if (this._dropTarget) {
      this.overlay
        .roundRect(0, 0, s, s, radius)
        .fill({ color: 0xFFFFFF, alpha: 0.16 });
      this.overlay
        .roundRect(1, 1, s - 2, s - 2, radius)
        .stroke({ color: 0xFFFFFF, width: 2, alpha: 0.5 });
    } else if (this._hover) {
      this.overlay
        .roundRect(0, 0, s, s, radius)
        .fill({ color: 0xFFFFFF, alpha: 0.1 });
    }
  }

  animateTo(targetGridIndex: number, ticker: Ticker, onComplete: () => void): void {
    if (this.container.parent) {
      this.container.parent.addChild(this.container);
    }

    const from = { x: this.container.x, y: this.container.y };
    const to   = this.gridToPixel(targetGridIndex);
    const tileSize  = this._tileSize;
    const startTime = performance.now();

    const tick = () => {
      const elapsed = performance.now() - startTime;
      const t    = Math.min(elapsed / ANIM_DURATION, 1);
      const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const arcY = -tileSize * 0.5 * Math.sin(Math.PI * t);
      this.container.x = from.x + (to.x - from.x) * ease;
      this.container.y = from.y + (to.y - from.y) * ease + arcY;
      if (t >= 1) {
        ticker.remove(tick);
        this.container.x = to.x;
        this.container.y = to.y;
        onComplete();
      }
    };

    ticker.add(tick);
  }

  pulse(ticker: Ticker, delay: number): Promise<void> {
    return new Promise(resolve => {
      const PULSE_DUR = PULSE_DUR_MS;
      const startAt  = performance.now() + delay;

      const tick = () => {
        const now = performance.now();
        if (now < startAt) return;

        const t     = Math.min((now - startAt) / PULSE_DUR, 1);
        const scale = 1 + 0.1 * Math.sin(Math.PI * t);
        this.container.scale.set(scale);

        if (t >= 1) {
          ticker.remove(tick);
          this.container.scale.set(1);
          resolve();
        }
      };

      ticker.add(tick);
    });
  }
}
